import { openid, Http, RtkqRequest, rtkq } from '@app/base';
import { baseApi, AppSettings } from '@app/base';
import { ChangePasswordRequest, User, UserLookup } from './usersTypes';
import { GetCurrentUserDocument, GetUserLookupsDocument } from './graphql';
import { patientsApi } from './patientsApi';

export function selectContext(args: { getState: () => any }): User {
  const state = args.getState() as any;
  return usersApi.endpoints.getContext.select(undefined)(state)?.data!;
}

export async function fetchIsUniqueUser(email: string) {
  const url = `/api/user/exists?email=${encodeURIComponent(email)}`;
  const response = await Http.anonymous().get<boolean>(url);
  return response.data;
}

export const usersApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getContext: build.query<User, RtkqRequest<void>>({
      queryFn: rtkq.graphql(GetCurrentUserDocument, (x) => x.currentUser as User),
      providesTags: (result) => [
        { type: 'main:user', id: result?.id ?? 'none' },
        { type: 'main:organization', id: result?.organizationId },
      ],
    }),

    getUserLookups: build.query<UserLookup[], RtkqRequest<void>>({
      queryFn: rtkq.graphql(GetUserLookupsDocument, (x) => x.users),
      providesTags: (result = []) => [
        { type: 'main:user', id: 'list' },
        ...result.map(({ id }) => ({ type: 'main:user' as const, id })),
      ],
    }),

    resendInvitation: build.mutation<void, RtkqRequest<{ id: string }>>({
      queryFn: rtkq.axios(({ id }) => Http.post(`/api/user/${id}/resend-invitation`)),
    }),

    changePassword: build.mutation<void, RtkqRequest<ChangePasswordRequest>>({
      queryFn: rtkq.axios((args) => {
        const uri = `${AppSettings.identityServerUrl}/api/user/password`;
        return Http.put(uri, args);
      }),
    }),

    changeActive: build.mutation<void, RtkqRequest<{ id: string; value: boolean }>>({
      queryFn: rtkq.axios(({ id, value }) => Http.put(`/api/user/${id}/active`, { id, value })),

      onQueryStarted: async ({ id, value }, { dispatch, queryFulfilled, getState }) => {
        const patch = dispatch(
          patientsApi.util.updateQueryData('getPatient', { id }, (draft) => {
            draft?.account && (draft.account.isActive = value);
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }

        const currentUser = selectContext({ getState });
        if (currentUser?.id === id) {
          await openid.signout();
        }
      },
    }),

    unlockUser: build.mutation<void, RtkqRequest<{ id: string }>>({
      queryFn: rtkq.axios(({ id }) => Http.put(`/api/user/${id}/unlock`)),
      invalidatesTags: (_, __, { id }) => [{ type: 'main:user', id }],
    }),

    resetTwoFactorMethod: build.mutation<void, RtkqRequest<{ id: string }>>({
      queryFn: rtkq.axios(({ id }) => Http.delete(`/api/user/${id}/two-factor-method`)),
    }),

    resetPassword: build.mutation<void, RtkqRequest<{ id: string }>>({
      queryFn: rtkq.axios(({ id }) => Http.post(`/api/user/reset-password/${id}`)),
    }),
  }),
});

export const {
  useGetContextQuery,
  useLazyGetUserLookupsQuery,
  useChangePasswordMutation,
  useResendInvitationMutation,
  useChangeActiveMutation,
  useUnlockUserMutation,
  useResetTwoFactorMethodMutation,
  useResetPasswordMutation,
} = usersApi;
